import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import {
  $getNearestNodeFromDOMNode,
  $getNodeByKey,
  type NodeKey,
} from "lexical";
import { useCallback, useEffect, useState } from "react";

import { $isProductBlockNode } from "./ProductBlockNode";
import { PRODUCT_BLOCK_CLASS } from "./ProductBlockNode";
import {
  $deleteProductWrapper,
  $findDiscoverContext,
  $hasDiscoverHeadingChip,
  $stripDiscoverHeadingChips,
  $unwrapContextKeepingCopy,
} from "./ProductBlockPlugin";
import {
  $isProductContextNode,
  PRODUCT_CONTEXT_CLASS,
  PRODUCT_CONTEXT_HEADER_CLASS,
  productModelLabel,
} from "./ProductContextNode";
import { DISCOVER_HEADING_LABEL } from "../mergeTagDefinitions";

/** The wrapper element a header control belongs to; its own DOM carries the key. */
function wrapperElementOf(control: HTMLElement): Element {
  return (
    control.closest(`.${PRODUCT_BLOCK_CLASS}, .${PRODUCT_CONTEXT_CLASS}`) ??
    control
  );
}

import { PRODUCT_GRID_MODELS } from "@/features/EmailEditor/plugins/productGridConstants";

// All grid models are context-capable in the text composer — including
// Discover, whose per-grid binding the reindex pass manages positionally.
const MODEL_OPTIONS = PRODUCT_GRID_MODELS.map(({ value, label }) => ({
  value,
  label,
}));

interface MenuTarget {
  element: HTMLElement;
  key: NodeKey;
  model: string;
  /** Another section already holds the message's one discover group. */
  discoverTaken: boolean;
}

/**
 * Click target on a context header: switch the context's model, or unwrap the
 * section back into ordinary copy. Block headers are plain labels — indices
 * are positional (the reindex pass owns them), and unwrapping a block would
 * orphan its product chips outside any guard.
 */
export function ProductBlockMenu(): React.ReactElement {
  const [editor] = useLexicalComposerContext();
  const [target, setTarget] = useState<MenuTarget | null>(null);
  // A model switch that would drop the Discover headline waits for an OK.
  const [pending, setPending] = useState<{
    key: NodeKey;
    model: string;
  } | null>(null);

  const close = useCallback(() => setTarget(null), []);

  useEffect(() => {
    const onClick = (event: Event) => {
      // The hover ✕ on either header deletes its wrapper outright — checked
      // first, since the context ✕ also sits inside the menu's header.
      const remove = (event.target as HTMLElement | null)?.closest?.(
        ".lexical-product-delete",
      );
      if (remove instanceof HTMLElement) {
        event.preventDefault();
        event.stopPropagation();
        editor.update(() => {
          const node = $getNearestNodeFromDOMNode(wrapperElementOf(remove));
          if ($isProductBlockNode(node) || $isProductContextNode(node)) {
            $deleteProductWrapper(node);
          }
        });
        return;
      }

      const header = (event.target as HTMLElement | null)?.closest?.(
        `.${PRODUCT_CONTEXT_HEADER_CLASS}`,
      );
      if (!(header instanceof HTMLElement)) return;
      event.preventDefault();
      event.stopPropagation();

      // editor.read, not getEditorState().read — $getNearestNodeFromDOMNode
      // needs an active editor, and several are mounted on this page.
      editor.read(() => {
        // The header row is unmanaged chrome; resolve from the section element.
        const node = $getNearestNodeFromDOMNode(wrapperElementOf(header));
        if ($isProductContextNode(node)) {
          const discover = $findDiscoverContext();
          setTarget({
            element: header,
            key: node.getKey(),
            model: node.getModel(),
            discoverTaken: discover !== null && !discover.is(node),
          });
        }
      });
    };

    return editor.registerRootListener((rootElement, prevRootElement) => {
      prevRootElement?.removeEventListener("click", onClick);
      rootElement?.addEventListener("click", onClick);
    });
  }, [editor]);

  const applyModel = useCallback(
    (key: NodeKey, model: string) => {
      editor.update(() => {
        const context = $getNodeByKey(key);
        if (!$isProductContextNode(context)) return;
        context.setModel(model);
        // The headline is bound by the discover section's open marker; under
        // another model it resolves to nothing, so it leaves with the switch.
        if (model !== "discover") $stripDiscoverHeadingChips(context);
      });
    },
    [editor],
  );

  const selectModel = useCallback(
    (model: string) => {
      const key = target?.key;
      const leavingDiscover = target?.model === "discover";
      close();
      if (key === undefined) return;
      // Losing the headline is the one switch that discards authored content,
      // so it asks first.
      if (leavingDiscover && model !== "discover") {
        let carriesHeading = false;
        editor.read(() => {
          const context = $getNodeByKey(key);
          if ($isProductContextNode(context)) {
            carriesHeading = $hasDiscoverHeadingChip(context);
          }
        });
        if (carriesHeading) {
          setPending({ key, model });
          return;
        }
      }
      applyModel(key, model);
    },
    [editor, target, close, applyModel],
  );

  const confirmPending = useCallback(() => {
    if (pending) applyModel(pending.key, pending.model);
    setPending(null);
  }, [pending, applyModel]);

  const unwrap = useCallback(() => {
    const key = target?.key;
    close();
    if (key === undefined) return;
    editor.update(() => {
      const wrapper = $getNodeByKey(key);
      if (!$isProductContextNode(wrapper)) return;
      // Keeps the words, not the tags: blocks dissolve and product chips are
      // stripped — an items.N chip is meaningless outside a context.
      $unwrapContextKeepingCopy(wrapper);
    });
  }, [editor, target, close]);

  return (
    <>
      {/* Outlives the menu: selecting a model closes that first. */}
      <Dialog open={pending !== null} onClose={() => setPending(null)}>
        <DialogTitle>Remove the {DISCOVER_HEADING_LABEL}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The {DISCOVER_HEADING_LABEL} merge tag is only available in
            Discover. Switching this section to{" "}
            {productModelLabel(pending?.model ?? "")} removes it from the
            message.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)}>Cancel</Button>
          <Button onClick={confirmPending} variant="contained" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
      {target && (
        <Menu open anchorEl={target.element} onClose={close}>
          {MODEL_OPTIONS.map((option) => {
            const taken = option.value === "discover" && target.discoverTaken;
            return (
              <MenuItem
                key={option.value}
                selected={option.value === target.model}
                disabled={taken}
                onClick={() => selectModel(option.value)}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 4,
                }}
              >
                {option.label}
                {taken && (
                  <Typography variant="caption" color="text.disabled">
                    Already in this message
                  </Typography>
                )}
              </MenuItem>
            );
          })}
          <Divider />
          <MenuItem onClick={unwrap}>Remove context (keep text)</MenuItem>
        </Menu>
      )}
    </>
  );
}
